import type { ChartOption } from "./chart-option.js"
import { ink, surface, type ThemeMode } from "../palette.js"
import { formatValue } from "../format.js"
import type { VizModel, VizSeries } from "../tile-model.js"
import { STALE_OPACITY, baseOption } from "./base.js"
import { CHART_TOOLTIP_CSS } from "../brand.js"

/**
 * ⚠ That distinction is the entire reason this file exists. `histogram` used to route straight
 * to `rankedBarOption`, which draws horizontal bars sorted by value — a **ranking**: "which
 * country set is largest". What `Countries per Country Set` actually asks is "how are country-set
 * sizes distributed: are most of them around 10, or spread between 9 and 250?".
 */

const MIN_BINS = 4
const MAX_BINS = 24
const FILL_GAP = 2

export type HistogramSeriesBins = {
  id: string
  name: string
  color: string
  state: VizSeries["state"]
  counts: number[]
}

export type HistogramBins = {
  edges: number[]
  labels: string[]
  series: HistogramSeriesBins[]
  population: number
  median: number | null
}

export function binCount(values: readonly number[]): number {
  const n = values.length
  if (n < 2) return 1

  const sorted = [...values].sort((a, b) => a - b)
  const min = sorted[0]!
  const max = sorted[n - 1]!
  if (max === min) return 1

  const iqr = quantile(sorted, 0.75) - quantile(sorted, 0.25)
  const width = iqr > 0 ? 2 * iqr * Math.pow(n, -1 / 3) : 0
  const bins = width > 0 ? Math.ceil((max - min) / width) : Math.ceil(Math.log2(n)) + 1

  return Math.min(MAX_BINS, Math.max(MIN_BINS, bins))
}

function quantile(sorted: readonly number[], q: number): number {
  const pos = (sorted.length - 1) * q
  const lo = Math.floor(pos)
  const hi = Math.ceil(pos)
  if (lo === hi) return sorted[lo]!
  return sorted[lo]! + (sorted[hi]! - sorted[lo]!) * (pos - lo)
}

export function histogramBins(model: VizModel): HistogramBins {
  const pooled = model.series.flatMap((s) => s.data.filter((v): v is number => v != null))
  if (pooled.length === 0)
    return { edges: [], labels: [], series: [], population: 0, median: null }

  const min = Math.min(...pooled)
  const max = Math.max(...pooled)
  const bins = binCount(pooled)
  const width = max === min ? 1 : (max - min) / bins

  const edges = Array.from({ length: bins + 1 }, (_, i) => min + i * width)
  edges[bins] = max

  const labels = edges.slice(0, -1).map((lo, i) => {
    const hi = edges[i + 1]!
    const fmt = (v: number) => formatValue(v, { valueType: model.unit, context: "axis" })
    return min === max ? fmt(lo) : `${fmt(lo)}–${fmt(hi)}`
  })

  const series = model.series.map((s) => ({
    id: s.id,
    name: s.name,
    color: s.color,
    state: s.state,
    counts: countInto(s.data, edges),
  }))

  return {
    edges,
    labels,
    series,
    population: pooled.length,
    median: quantile([...pooled].sort((a, b) => a - b), 0.5),
  }
}

export function medianAxisPosition(bins: HistogramBins): number | null {
  const { median, edges } = bins
  if (median == null || edges.length < 2) return null

  const count = edges.length - 1
  const min = edges[0]!
  const max = edges[count]!
  if (max === min) return 0

  const width = (max - min) / count
  const idx = Math.min(count - 1, Math.floor((median - min) / width))
  const within = (median - (min + idx * width)) / width
  return idx - 0.5 + within
}

function countInto(data: readonly (number | null)[], edges: readonly number[]): number[] {
  const bins = edges.length - 1
  const counts = new Array<number>(bins).fill(0)
  const min = edges[0]!
  const max = edges[bins]!

  for (const v of data) {
    if (v == null) continue
    if (v < min || v > max) continue
    const width = (max - min) / bins
    const idx = width > 0 ? Math.min(bins - 1, Math.floor((v - min) / width)) : 0
    counts[idx] = (counts[idx] ?? 0) + 1
  }
  return counts
}

export type HistogramOptions = {
  mode: ThemeMode
  model: VizModel
  showDataLabels?: boolean
  legendPosition?: "bottom" | "right"
}

export function histogramOption({
  mode,
  model,
  showDataLabels,
  legendPosition,
}: HistogramOptions): ChartOption {
  const c = ink(mode)
  const bins = histogramBins(model)
  const base = baseOption({ mode, model, ...(legendPosition ? { legendPosition } : {}) })

  const medianAt = medianAxisPosition(bins)

  const series = bins.series.map((s, seriesIndex) => {
    const stale = s.state === "stale"
    return {
      type: "bar" as const,
      name: s.name,
      data: s.counts,
      barCategoryGap: "2%",
      barGap: "0%",
      itemStyle: {
        color: s.color,
        opacity: stale ? STALE_OPACITY : 1,
        borderColor: surface(mode),
        borderWidth: FILL_GAP,
      },
      emphasis: { itemStyle: { opacity: 0.85 } },
      label: showDataLabels
        ? {
            show: true,
            position: "top" as const,
            color: c.secondary,
            fontSize: 11,
            formatter: (p: { value: number }) => (p.value > 0 ? String(p.value) : ""),
          }
        : { show: false },
      ...(seriesIndex === 0 && medianAt != null
        ? {
            markLine: {
              silent: true,
              symbol: "none" as const,
              lineStyle: { color: c.baseline, width: 1, type: "dashed" as const },
              label: {
                show: true,
                position: "end" as const,
                color: c.secondary,
                fontSize: 11,
                formatter: () =>
                  `median ${formatValue(bins.median, { valueType: model.unit, context: "label" })}`,
              },
              data: [{ xAxis: medianAt }],
            },
          }
        : {}),
    }
  })

  return {
    ...base,
    xAxis: {
      ...base.xAxis!,
      type: "category",
      data: bins.labels,
      axisLabel: { ...base.xAxis!.axisLabel, rotate: bins.labels.length > 8 ? 30 : 0 },
    },
    yAxis: {
      ...base.yAxis!,
      axisLabel: {
        ...base.yAxis!.axisLabel,
        formatter: (v: number) => formatValue(v, { valueType: "count", context: "axis" }),
      },
      name: bins.population > 0 ? `n = ${bins.population}` : undefined,
    },
    tooltip: histogramTooltip(mode, bins),
    series,
  }
}

function histogramTooltip(mode: ThemeMode, bins: HistogramBins) {
  const c = ink(mode)

  return {
    trigger: "item" as const,
    backgroundColor: surface(mode),
    borderColor: c.gridline,
    borderWidth: 1,
    padding: [8, 10] as readonly number[],
    textStyle: { color: c.primary, fontSize: 12 },
    extraCssText: CHART_TOOLTIP_CSS,
    formatter: (raw: unknown) => {
      const p = raw as { dataIndex: number; seriesName?: string; value?: number; color?: string }
      const label = bins.labels[p.dataIndex] ?? ""
      const n = p.value ?? 0
      const total = bins.series.find((s) => s.name === p.seriesName)?.counts.reduce((a, b) => a + b, 0) ?? 0
      const share = total > 0 ? ` (${Math.round((n / total) * 100)}%)` : ""
      const mark = `<span style="display:inline-block;width:12px;height:2px;background:${p.color};vertical-align:middle;margin-right:6px"></span>`

      return `<div style="color:${c.secondary};font-size:11px;margin-bottom:2px">${label}</div>
        <div style="display:flex;align-items:center;gap:8px">
          <span>${mark}</span>
          <strong style="color:${c.primary};font-variant-numeric:tabular-nums">${n}${share}</strong>
          <span style="color:${c.secondary}">${p.seriesName ?? ""}</span>
        </div>`
    },
  }
}
